<template>
  <div class="row g-3">
    <div v-if="checkins?.length > 0 && !isEditMode && checkInEnabled && checkInInstant" class="col-12 mt-20">
      <div class="form-check">
        <input
          id="usarDatosCheckin"
          class="form-check-input"
          type="checkbox"
          :checked="usarDatosCheckin"
          @change="handleUsarDatosCheckinChange" />
        <label class="form-check-label" for="usarDatosCheckin">
          {{ $t('reserva.datosFiscales.usarDatosAsistente') }}
        </label>
      </div>

      <div v-if="usarDatosCheckin" class="mb-2 mt-10">
        <InputSelector
          v-model="selectedCheckinIndex"
          :options="checkinsOptions"
          :placeholder="$t('reserva.checkIn.placeholders.seleccionaTipo')" />
      </div>
    </div>

    <div v-if="!tipo" class="col-12 mt-20">
      <label class="medium p small-m text-landing mb-2">{{ $t('reserva.datosFiscales.tipo') }}</label>
      <InputSelector
        v-model="formularioData.tipo"
        :options="tipoDatosFiscalesOptions"
        :placeholder="$t('reserva.checkIn.placeholders.seleccionaTipo')" />
    </div>

    <template
      v-if="
        formularioData.tipo === TIPO_DATOS_FISCALES.EMPRESA || formularioData.tipo === TIPO_DATOS_FISCALES.AUTONOMO
      ">
      <div class="col-12">
        <label class="medium p small-m text-landing small mb-2">{{ $t('reserva.datosFiscales.razonSocial') }}</label>
        <input
          v-model="formularioData.razonSocial"
          :class="{ 'is-invalid': errors.razonSocial }"
          class="form-control bg-light border-0 py-3"
          :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
          @blur="validateField(CAMPOS_FORMULARIO.RAZON_SOCIAL)" />
        <small v-if="errors.razonSocial" class="text-danger d-block sub mt-1">
          {{ errors.razonSocial }}
        </small>
      </div>
    </template>

    <template v-if="formularioData.tipo === TIPO_DATOS_FISCALES.PARTICULAR">
      <div class="col-12 col-md-6">
        <label class="medium p small-m text-landing small mb-2">{{ $t('reserva.datosFiscales.nombre') }}</label>
        <input
          v-model="formularioData.nombre"
          :class="{ 'is-invalid': errors.nombre }"
          class="form-control bg-light border-0 py-3"
          :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
          @blur="validateField(CAMPOS_FORMULARIO.NOMBRE)" />
        <small v-if="errors.nombre" class="text-danger d-block sub mt-1">
          {{ errors.nombre }}
        </small>
      </div>
      <div class="col-12 col-md-6">
        <label class="medium p small-m text-landing small mb-2">{{ $t('reserva.datosFiscales.apellidos') }}</label>
        <input
          v-model="formularioData.apellidos"
          :class="{ 'is-invalid': errors.apellidos }"
          class="form-control bg-light border-0 py-3"
          :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
          @blur="validateField(CAMPOS_FORMULARIO.APELLIDOS)" />
        <small v-if="errors.apellidos" class="text-danger d-block sub mt-1">
          {{ errors.apellidos }}
        </small>
      </div>
    </template>

    <template v-if="!ocultarContacto">
      <div class="col-12 col-md-6">
        <label class="medium p small-m small-m text-landing small mb-2">{{
          $t('reserva.datosFiscales.telefono')
        }}</label>
        <InputTelefono
          v-model="formularioData.telefono"
          :tipo="TIPOS_TELEFONO.TIPO_B"
          :has-error="submitted && !!errors.telefono"
          :class="{ 'is-field-error': submitted && !!errors.telefono }"
          :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
          required />
        <small v-if="submitted && errors.telefono" class="text-danger d-block sub mt-1">
          {{ errors.telefono }}
        </small>
      </div>

      <div class="col-12 col-md-6">
        <label class="medium p small-m text-landing small mb-2">{{ $t('reserva.datosFiscales.email') }}</label>
        <input
          v-model="formularioData.email"
          :class="{ 'is-invalid': errors.email }"
          class="form-control bg-light border-0 py-3"
          :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
          @blur="validateField(CAMPOS_FORMULARIO.EMAIL)" />
        <small v-if="errors.email" class="text-danger d-block sub mt-1">
          {{ errors.email }}
        </small>
      </div>
    </template>

    <div class="col-12 col-md-6">
      <label class="medium p small-m text-landing small mb-2">{{ $t('reserva.datosFiscales.pais') }}</label>
      <InputSelector
        v-model="formularioData.paisCode"
        :has-error="submitted && errors.paisCode"
        :class="{ 'is-field-error': submitted && errors.paisCode }"
        :options="countryList"
        :placeholder="$t('reserva.checkIn.placeholders.seleccionaPais')" />
      <small v-if="submitted && errors.paisCode" class="text-danger d-block sub mt-1">
        {{ errors.paisCode }}
      </small>
    </div>

    <div class="col-12 col-md-6">
      <div class="d-flex align-items-center gap-1 mb-2">
        <label
          v-if="formularioData.tipo === TIPO_DATOS_FISCALES.PARTICULAR"
          class="medium p small-m text-landing small mb-0"
          >{{ $t('reserva.datosFiscales.idFiscalParticular') }}</label
        >
        <label v-else class="medium p small-m text-landing small mb-0">{{
          $t('reserva.datosFiscales.idFiscalEmpresa')
        }}</label>
        <InfoIcon size="12px" class="opacity-80 flex-shrink-0" :message-info="documentoTooltip" />
      </div>
      <input
        :value="formularioData.idFiscal"
        :class="{ 'is-invalid': documentoIdentidadErrorMessage }"
        class="form-control bg-light border-0 py-3"
        :placeholder="idFiscalPlaceholder"
        @input="formularioData.idFiscal = formatDocumentoInput($event)"
        @blur="validateField(CAMPOS_FORMULARIO.ID_FISCAL)" />
      <small v-if="documentoIdentidadErrorMessage" class="text-danger d-block sub mt-1">
        {{ documentoIdentidadErrorMessage }}
      </small>
    </div>

    <div class="col-12 col-md-6">
      <label class="medium p small-m text-landing small mb-2">{{ $t('reserva.datosFiscales.direccion') }}</label>
      <input
        v-model="formularioData.direccion"
        :class="{ 'is-invalid': errors.direccion }"
        class="form-control bg-light border-0 py-3"
        :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
        @blur="validateField(CAMPOS_FORMULARIO.DIRECCION)" />
      <small v-if="errors.direccion" class="text-danger d-block sub mt-1">
        {{ errors.direccion }}
      </small>
    </div>

    <div class="col-12 col-md-6">
      <label class="medium p small-m text-landing small mb-2">{{ $t('reserva.datosFiscales.codigoPostal') }}</label>
      <input
        v-model="formularioData.codigoPostal"
        :class="{ 'is-invalid': codigoPostalErrorMessage }"
        class="form-control bg-light border-0 py-3"
        :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
        @blur="validateField(CAMPOS_FORMULARIO.CODIGO_POSTAL)" />
      <small v-if="codigoPostalErrorMessage" class="text-danger d-block sub mt-1">
        {{ codigoPostalErrorMessage }}
      </small>
    </div>

    <div class="col-12 col-md-6">
      <label class="medium p small-m text-landing small mb-2">{{ $t('reserva.datosFiscales.poblacion') }}</label>
      <input
        v-model="formularioData.poblacion"
        :class="{ 'is-invalid': errors.poblacion }"
        class="form-control bg-light border-0 py-3"
        :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
        @blur="validateField(CAMPOS_FORMULARIO.POBLACION)" />
      <small v-if="errors.poblacion" class="text-danger d-block sub mt-1">
        {{ errors.poblacion }}
      </small>
    </div>

    <div class="col-12 col-md-6">
      <label class="medium p small-m text-landing small mb-2">{{ $t('reserva.datosFiscales.provincia') }}</label>
      <input
        v-model="formularioData.provincia"
        :class="{ 'is-invalid': errors.provincia }"
        class="form-control bg-light border-0 py-3"
        :placeholder="$t('reserva.checkIn.placeholders.escribeTuRespuesta')"
        @blur="validateField(CAMPOS_FORMULARIO.PROVINCIA)" />
      <small v-if="errors.provincia" class="text-danger d-block sub mt-1">
        {{ errors.provincia }}
      </small>
    </div>
  </div>
</template>

<script setup>
  import { computed, onBeforeMount, reactive, ref, watch } from 'vue';
  import { useUserStore } from '@/store_pinia/userStore';
  import { informacionHabitacion } from '@/store_pinia/informacionHabitacion';
  import { useI18n } from 'vue-i18n';
  import InputSelector from '@/components/inputs/InputSelector.vue';
  import { TIPO_DATOS_FISCALES } from '@/constants/datosFiscalesConstants';
  import { CAMPOS_FORMULARIO } from '@/constants/formularioConstants';
  import InputTelefono from '@/components/inputs/InputTelefono.vue';
  import InfoIcon from '@/components/Icons/InfoIcon.vue';
  import { TIPOS_TELEFONO } from '@/constants/inputsConstants';
  import { validarCodigoPostal } from '@/helpers/stringUtiles';
  import { checkDocumento, formatDocumentoInput } from '@/helpers/documentoIdentidadUtils';
  import { COUNTRY_CODES } from '@/constants/countryConstants';
  import { getCountryOptions } from '@/helpers/countryUtils';

  const props = defineProps({
    checkins: {
      type: Array,
      default: () => [],
    },
    datosFiscales: {
      type: Object,
      default: null,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    tipo: {
      type: String,
      default: null,
    },
    ocultarContacto: {
      type: Boolean,
      default: false,
    },
    paisCodeDefault: {
      type: String,
      default: null,
    },
  });

  const { t } = useI18n();
  const userStore = useUserStore();
  const storeHabitacion = informacionHabitacion();
  const countryList = ref({});
  const usarDatosCheckin = ref(false);
  const selectedCheckinIndex = ref(null);
  const submitted = ref(false);
  const formularioData = reactive({
    tipo: props.tipo || TIPO_DATOS_FISCALES.PARTICULAR,
    razonSocial: '',
    nombre: '',
    apellidos: '',
    paisCode: '',
    direccion: '',
    poblacion: '',
    codigoPostal: '',
    provincia: '',
    idFiscal: '',
    email: '',
    telefono: '',
  });
  const errors = reactive({
    razonSocial: '',
    nombre: '',
    apellidos: '',
    paisCode: '',
    direccion: '',
    poblacion: '',
    codigoPostal: '',
    provincia: '',
    idFiscal: '',
    email: '',
    telefono: '',
  });

  const tipoDatosFiscalesOptions = {
    [TIPO_DATOS_FISCALES.EMPRESA]: t('reserva.datosFiscales.tipoEmpresa'),
    [TIPO_DATOS_FISCALES.AUTONOMO]: t('reserva.datosFiscales.tipoAutonomo'),
    [TIPO_DATOS_FISCALES.PARTICULAR]: t('reserva.datosFiscales.tipoParticular'),
  };

  const checkInEnabled = computed(() => storeHabitacion.getViaje?.formularioInscripcion?.checkInEnabled);
  const checkInInstant = computed(() => storeHabitacion.getViaje?.formularioInscripcion?.checkInInstant);

  const checkinsOptions = computed(() => {
    if (!props.checkins || props.checkins.length === 0) return {};
    return props.checkins.reduce((acc, checkin, index) => {
      acc[index] = `${checkin.nombre} ${checkin.apellidos}`.trim();
      return acc;
    }, {});
  });

  const isEmpresaFiscal = computed(() => formularioData.tipo === TIPO_DATOS_FISCALES.EMPRESA);

  const documentoTooltip = computed(() => {
    if (!formularioData.paisCode) return t('reserva.checkIn.tooltipTiposDocumentoIdentidad');
    return documentoIdentidadResult().hints;
  });

  const idFiscalPlaceholder = computed(() => {
    if (!formularioData.paisCode) return t('reserva.checkIn.placeholders.escribeTuRespuesta');
    return documentoIdentidadResult().example;
  });

  const documentoIdentidadErrorMessage = computed(() => {
    if (errors.idFiscal) {
      return errors.idFiscal;
    }
    if (!formularioData.paisCode || !formularioData.idFiscal) return '';
    const result = documentoIdentidadResult();
    if (result.isValid) return '';
    return invalidDocumentMessage(result);
  });

  const codigoPostalErrorMessage = computed(() => {
    if (errors.codigoPostal) {
      return errors.codigoPostal;
    }
    if (
      formularioData.paisCode === COUNTRY_CODES.SPAIN &&
      formularioData.codigoPostal &&
      !validarCodigoPostal(String(formularioData.codigoPostal).trim())
    ) {
      return t('reserva.checkIn.error.spain.cp.notValid');
    }
    return '';
  });

  function documentoIdentidadResult() {
    return checkDocumento(formularioData.paisCode, formularioData.idFiscal, {
      isEntity: isEmpresaFiscal.value,
    });
  }

  function invalidDocumentMessage(result) {
    return t('reserva.checkIn.error.documento.notValidWithHints', { hints: result.hints });
  }

  function clearErrors() {
    Object.keys(errors).forEach(key => {
      errors[key] = '';
    });
  }

  function validateFields() {
    clearErrors();
    const requiredMessage = t('reserva.validation.requiredField');
    if (formularioData.tipo === TIPO_DATOS_FISCALES.EMPRESA || formularioData.tipo === TIPO_DATOS_FISCALES.AUTONOMO) {
      if (!formularioData.razonSocial) errors.razonSocial = requiredMessage;
    }
    if (formularioData.tipo === TIPO_DATOS_FISCALES.PARTICULAR) {
      if (!formularioData.nombre) errors.nombre = requiredMessage;
      if (!formularioData.apellidos) errors.apellidos = requiredMessage;
    }
    if (!formularioData.paisCode) errors.paisCode = requiredMessage;
    if (!formularioData.direccion) errors.direccion = requiredMessage;
    if (!formularioData.poblacion) errors.poblacion = requiredMessage;
    if (!formularioData.codigoPostal) errors.codigoPostal = requiredMessage;
    if (!formularioData.provincia) errors.provincia = requiredMessage;
    if (!formularioData.idFiscal) errors.idFiscal = requiredMessage;
    if (!props.ocultarContacto) {
      if (!formularioData.email) errors.email = requiredMessage;
      if (!formularioData.telefono) errors.telefono = requiredMessage;
    }

    if (!errors.idFiscal && formularioData.paisCode && formularioData.idFiscal) {
      const result = documentoIdentidadResult();
      if (!result.isValid) {
        errors.idFiscal = invalidDocumentMessage(result);
      }
    }
    if (
      !errors.codigoPostal &&
      formularioData.paisCode === COUNTRY_CODES.SPAIN &&
      !validarCodigoPostal(formularioData.codigoPostal.trim())
    ) {
      errors.codigoPostal = t('reserva.checkIn.error.spain.cp.notValid');
    }

    return !Object.values(errors).some(Boolean);
  }

  function validateAndShowErrors() {
    submitted.value = true;
    return validateFields();
  }

  function validateField(field) {
    errors[field] = !String(formularioData[field] ?? '').trim() ? t('reserva.validation.requiredField') : '';
  }

  function fillWithDatosUsuario() {
    if (props.isEditMode || props.datosFiscales) {
      return;
    }

    const usuario = userStore.getUsuario;
    if (!formularioData.nombre) formularioData.nombre = usuario?.nombre || '';
    if (!formularioData.apellidos) formularioData.apellidos = usuario?.apellidos || '';
    if (!formularioData.email) formularioData.email = usuario?.email || '';
    if (!formularioData.direccion) formularioData.direccion = usuario?.direccion || '';
    if (!formularioData.poblacion) formularioData.poblacion = usuario?.poblacion || '';
    if (!formularioData.provincia) formularioData.provincia = usuario?.provincia || '';
    if (!formularioData.codigoPostal) formularioData.codigoPostal = usuario?.codigoPostal || '';
    if (!formularioData.telefono) formularioData.telefono = usuario?.telefono || '';
    if (!formularioData.paisCode) {
      formularioData.paisCode = usuario?.paisCode || usuario?.paisId || props.paisCodeDefault || '';
    }
  }

  function initCountrySelector() {
    countryList.value = getCountryOptions();
  }

  function fillWithCheckin(data) {
    if (!data) return;
    formularioData.direccion = data.direccion || '';
    formularioData.codigoPostal = data.codigoPostal || '';
    formularioData.paisCode = data.pais || '';
    formularioData.nombre = data.nombre || '';
    formularioData.apellidos = data.apellidos || '';
    formularioData.idFiscal = data.dni || '';
    formularioData.poblacion = data.poblacion || '';
    formularioData.provincia = data.provincia || '';
    clearErrors();
  }

  function cleanCheckin() {
    formularioData.direccion = '';
    formularioData.codigoPostal = '';
    formularioData.paisCode = '';
    formularioData.nombre = '';
    formularioData.apellidos = '';
    formularioData.idFiscal = '';
    formularioData.poblacion = '';
    formularioData.provincia = '';
    clearErrors();
  }

  function handleUsarDatosCheckinChange(event) {
    usarDatosCheckin.value = event.target.checked;
    selectedCheckinIndex.value = event.target.checked && props.checkins?.length > 0 ? 0 : null;
  }

  function resetForm() {
    formularioData.tipo = props.tipo || TIPO_DATOS_FISCALES.PARTICULAR;
    formularioData.razonSocial = '';
    formularioData.nombre = '';
    formularioData.apellidos = '';
    formularioData.paisCode = props.paisCodeDefault || '';
    formularioData.direccion = '';
    formularioData.poblacion = '';
    formularioData.codigoPostal = '';
    formularioData.provincia = '';
    formularioData.idFiscal = '';
    formularioData.email = '';
    formularioData.telefono = '';
    clearErrors();
    submitted.value = false;
    fillWithDatosUsuario();
  }

  function loadDatosFiscales(datosFiscales) {
    if (datosFiscales) {
      formularioData.tipo = props.tipo || datosFiscales.tipo || TIPO_DATOS_FISCALES.PARTICULAR;
      formularioData.razonSocial = datosFiscales.razonSocial || '';
      formularioData.nombre = datosFiscales.nombre || '';
      formularioData.apellidos = datosFiscales.apellidos || '';
      formularioData.paisCode = datosFiscales.paisCode || props.paisCodeDefault || '';
      formularioData.direccion = datosFiscales.direccion || '';
      formularioData.poblacion = datosFiscales.poblacion || '';
      formularioData.codigoPostal = datosFiscales.codigoPostal || '';
      formularioData.provincia = datosFiscales.provincia || '';
      formularioData.idFiscal = datosFiscales.idFiscal || '';
      formularioData.email = datosFiscales.email || '';
      formularioData.telefono = datosFiscales.telefono || '';
      clearErrors();
    }
  }

  watch(
    () => [usarDatosCheckin.value, selectedCheckinIndex.value],
    ([isChecked, index]) => {
      if (props.isEditMode || props.datosFiscales) {
        return;
      }

      if (!isChecked) {
        cleanCheckin();
        fillWithDatosUsuario();
      } else if (index !== null && props.checkins[index]) {
        fillWithCheckin(props.checkins[index]);
      }
    }
  );

  watch(
    () => props.checkins,
    newCheckins => {
      if (usarDatosCheckin.value && selectedCheckinIndex.value !== null && !props.isEditMode && !props.datosFiscales) {
        const checkinSeleccionado = newCheckins[selectedCheckinIndex.value];
        if (checkinSeleccionado) {
          fillWithCheckin(checkinSeleccionado);
        }
      }
    },
    { deep: true }
  );

  watch(
    formularioData,
    () => {
      if (!submitted.value) return;
      validateFields();
    },
    { deep: true }
  );

  onBeforeMount(() => {
    initCountrySelector();
    if (props.tipo) {
      formularioData.tipo = props.tipo;
    }
    if (props.datosFiscales) {
      loadDatosFiscales(props.datosFiscales);
    } else {
      fillWithDatosUsuario();
      if (!formularioData.paisCode && props.paisCodeDefault) {
        formularioData.paisCode = props.paisCodeDefault;
      }
    }
  });

  defineExpose({
    formularioData,
    errors,
    validateAndShowErrors,
    resetForm,
    loadDatosFiscales,
    fillWithCheckin,
  });
</script>

<style scoped lang="scss">
  .form-control,
  .form-select {
    background-color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.02) !important;
    box-shadow: 0 0 3px 0 rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.35) inset !important;
    border: none !important;
    height: 40px !important;
    padding: 0.375rem 0.75rem !important;
    text-align: left !important;
    color: var(--landing-text-color, inherit) !important;

    &::placeholder {
      color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.45) !important;
      opacity: 1;
    }

    &:disabled {
      background-color: var(--landing-background-color, var(--bs-white)) !important;
      opacity: 0.7 !important;
    }
  }

  :deep(.input-selector-wrapper .form-control) {
    background-color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.02) !important;
    box-shadow: 0 0 3px 0 rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.35) inset !important;
    border: none !important;
    color: var(--landing-text-color, inherit) !important;

    &::placeholder {
      color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.45) !important;
      opacity: 1;
    }
  }

  :deep(.input-telefono-tipo-b) {
    background-color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.02) !important;
    box-shadow: 0 0 3px 0 rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.35) inset !important;
    border: none !important;
    color: var(--landing-text-color, inherit) !important;
  }

  :deep(.input-telefono-tipo-b .vti__input) {
    color: var(--landing-text-color, inherit) !important;
    background: transparent !important;

    &::placeholder {
      color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.45) !important;
      opacity: 1;
    }
  }

  :deep(.input-telefono-tipo-b .vti__dropdown),
  :deep(.input-telefono-tipo-b .vti__selection) {
    color: var(--landing-text-color, inherit) !important;
  }

  .form-control.is-invalid {
    border: 1px solid var(--bs-danger) !important;
  }

  :deep(.input-selector-wrapper.is-field-error .form-control) {
    border: 1px solid var(--bs-danger) !important;
  }

  :deep(.input-telefono-tipo-b.is-field-error) {
    border: 1px solid var(--bs-danger) !important;
  }

  .form-check-label {
    color: var(--landing-text-color, inherit);
  }

  .form-check-input[type='checkbox'] {
    cursor: pointer;
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.3);

    &:checked {
      background-color: var(--bs-secondary);
      border-color: var(--bs-secondary);
    }

    &:focus {
      box-shadow: none;
    }
  }

  @media screen and (max-width: 768px) {
    .form-control,
    .form-select,
    .form-check-label,
    input::placeholder,
    select,
    .input-group-text {
      font-size: 13px !important;
    }
  }
</style>
